"use client"

import { useState } from "react"
import { HexColorPicker } from "react-colorful"
import { BottomSheet } from "./bottom-sheet"
import { Button } from "./button"

export interface DesignBottomSheetProps {
    isOpen: boolean
    onClose: () => void
    pageBackground: string
    profileCard: string
    titleColor: string
    ctaColor: string
    textColor: string
    onChange: (colors: {
        pageBackground?: string
        profileCard?: string
        titleColor?: string
        ctaColor?: string
        textColor?: string
    }) => void
    onSave?: () => void
    isSaving?: boolean
}

type ColorKey = "pageBackground" | "profileCard" | "titleColor" | "ctaColor" | "textColor"

const colorLabels: Record<ColorKey, string> = {
    pageBackground: "Page Background",
    profileCard: "Profile Card",
    titleColor: "Title",
    ctaColor: "CTA",
    textColor: "Page Text",
}

export function DesignBottomSheet({
    isOpen,
    onClose,
    pageBackground,
    profileCard,
    titleColor,
    ctaColor,
    textColor,
    onChange,
    onSave,
    isSaving = false,
}: DesignBottomSheetProps) {
    const colors: Record<ColorKey, string> = {
        pageBackground,
        profileCard,
        titleColor,
        ctaColor,
        textColor,
    }

    const handleColorChange = (key: ColorKey, value: string) => {
        onChange({ [key]: value })
    }

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            className="bg-[#FFFAEF] px-3 pb-6 pt-2"
            contentClassName="bottom-0! fixed!"
            overlayClassName="bg-transparent backdrop-blur-none"
            showDragHandle
            title="Design"
        >
            <div className="space-y-2">
                {(Object.keys(colorLabels) as ColorKey[]).map((key) => (
                    <ColorRow
                        key={key}
                        label={colorLabels[key]}
                        value={colors[key]}
                        onChange={(value) => handleColorChange(key, value)}
                    />
                ))}

                <div className="pt-3">
                    <Button 
                        className="w-full rounded-lg" 
                        onClick={onSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save Design"}
                    </Button>
                </div>
            </div>
        </BottomSheet>
    )
}

function ColorRow({
    label,
    value,
    onChange,
}: {
    label: string
    value: string
    onChange: (value: string) => void
}) {
    const [isPickerOpen, setIsPickerOpen] = useState(false)

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-black bg-white px-3 py-3">
                <span className="text-sm font-medium text-[#344054]">{label}</span>
                <button
                    type="button"
                    onClick={() => setIsPickerOpen(!isPickerOpen)}
                    className="h-4 w-4 rounded-full flex-shrink-0 border cursor-pointer hover:scale-105 transition-transform"
                    style={{ backgroundColor: value }}
                    aria-label={`${isPickerOpen ? "Close" : "Open"} color picker for ${label}`}
                />
            </div>
            {isPickerOpen && (
                <div className="rounded-lg border border-black bg-white p-3">
                    <div className="mb-3">
                        <input
                            type="text"
                            value={value.toUpperCase()}
                            onChange={(e) => {
                                const hex = e.target.value
                                if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                                    onChange(hex)
                                }
                            }}
                            className="w-full rounded border border-black px-3 py-2 text-sm font-mono uppercase"
                            placeholder="#FFFFFF"
                            maxLength={7}
                        />
                    </div>
                    <HexColorPicker 
                        color={value} 
                        onChange={onChange} 
                        style={{ 
                            width: "100%",
                            height: "200px"
                        }} 
                    />
                </div>
            )}
        </div>
    )
}
